import { APP_NAME, APP_TAGLINE, BRAND_LOGO, BRAND_SKY } from "@/lib/brand";
import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowRight, MailCheck, Loader2 } from "lucide-react";

function Shell({ children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="signup-page">
      <div
        className="relative hidden lg:flex flex-col justify-between p-12 text-white overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(37,99,235,0.88), rgba(59,130,246,0.42)), url(${BRAND_SKY})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative z-10 inline-flex items-center bg-white/95 rounded-xl px-3.5 py-2 shadow-sm w-fit">
          <img src={BRAND_LOGO} alt={APP_NAME} className="h-[31px] w-auto object-contain" />
        </div>
        <div className="relative z-10 fade-in-up">
          <p className="overline" style={{ color: "rgba(255,255,255,0.85)" }}>Create your account</p>
          <h1 className="mt-4 text-4xl xl:text-6xl font-extrabold tracking-tight leading-[1.05] max-w-md">{APP_TAGLINE}</h1>
          <p className="mt-5 text-white/85 max-w-md leading-relaxed">
            Set up your Airava workspace in a moment — projects, workstreams and ERP handover, clearly in one place.
          </p>
        </div>
        <div className="relative z-10 text-xs text-white/70">© {new Date().getFullYear()} {APP_NAME} · {APP_TAGLINE}</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-6">
            <img src={BRAND_LOGO} alt={APP_NAME} className="h-8 w-auto object-contain" />
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

export default function SignupPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", confirm: "", company_name: "" });
  const [accept, setAccept] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [resending, setResending] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const valid =
    form.first_name.trim() &&
    form.last_name.trim() &&
    /\S+@\S+\.\S+/.test(form.email) &&
    form.password.length >= 8 &&
    form.password === form.confirm &&
    accept;

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) { toast.error("Passwords do not match"); return; }
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (!accept) { toast.error("Please accept the Terms of Use and Privacy Policy"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/register", {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        password: form.password,
        company_name: form.company_name.trim() || null,
        accept_privacy: true,
        accept_terms: true,
      });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email: form.email.trim() });
      toast.success("Verification email sent again. Check your inbox.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setResending(false);
    }
  };

  if (sent) {
    return (
      <Shell>
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm" data-testid="signup-sent-state">
          <div className="mx-auto h-12 w-12 rounded-full bg-blue-100 grid place-items-center">
            <MailCheck className="h-6 w-6 text-[#2563EB]" />
          </div>
          <h1 className="mt-4 text-xl font-bold tracking-tight text-slate-900">Check your email</h1>
          <p className="mt-2 text-sm text-slate-500">
            We sent a verification link to <span className="font-medium text-slate-700" data-testid="signup-sent-email">{form.email.trim()}</span>.
            Click it to activate your account, then sign in.
          </p>
          <div className="mt-6 flex flex-col gap-2">
            <Button variant="outline" onClick={resend} disabled={resending} data-testid="signup-resend-btn">
              {resending ? "Sending…" : "Resend verification email"}
            </Button>
            <Button variant="ghost" onClick={() => navigate("/login")} data-testid="signup-to-login">Back to sign in</Button>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <p className="overline">Get started</p>
      <h1 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">Create your {APP_NAME} account</h1>
      <p className="mt-2 text-sm text-slate-500">No invitation needed — create a workspace in a minute.</p>

      <form onSubmit={submit} className="mt-6 rounded-xl border border-slate-200 bg-white p-6 shadow-sm space-y-4" data-testid="signup-form">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-medium text-slate-600">First name</Label>
            <Input value={form.first_name} onChange={set("first_name")} placeholder="Jane" className="mt-1" data-testid="signup-first-name" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-600">Last name</Label>
            <Input value={form.last_name} onChange={set("last_name")} placeholder="Doe" className="mt-1" data-testid="signup-last-name" />
          </div>
        </div>
        <div>
          <Label className="text-xs font-medium text-slate-600">Work email</Label>
          <Input type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className="mt-1" data-testid="signup-email" />
        </div>
        <div>
          <Label className="text-xs font-medium text-slate-600">Company / Workspace name <span className="text-slate-400">(optional)</span></Label>
          <Input value={form.company_name} onChange={set("company_name")} placeholder="Acme Inc." className="mt-1" data-testid="signup-company" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-medium text-slate-600">Password</Label>
            <Input type="password" value={form.password} onChange={set("password")} placeholder="min 8 characters" className="mt-1" data-testid="signup-password" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-600">Confirm password</Label>
            <Input type="password" value={form.confirm} onChange={set("confirm")} placeholder="••••••••" className="mt-1" data-testid="signup-confirm" />
          </div>
        </div>
        {form.confirm && form.password !== form.confirm && (
          <p className="text-xs text-red-600" data-testid="signup-pw-mismatch">Passwords do not match.</p>
        )}
        <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer pt-1">
          <Checkbox checked={accept} onCheckedChange={(v) => setAccept(!!v)} className="mt-0.5" data-testid="signup-accept-terms" />
          <span>
            I agree to the <a href="/terms" target="_blank" rel="noreferrer" className="text-[#2563EB] hover:underline">Terms of Use</a> and{" "}
            <a href="/privacy" target="_blank" rel="noreferrer" className="text-[#2563EB] hover:underline">Privacy Policy</a>.
          </span>
        </label>
        <Button type="submit" disabled={submitting || !valid} className="w-full bg-[#2563EB] hover:bg-[#1d4ed8] h-10" data-testid="signup-submit">
          {submitting ? "Creating account…" : "Create account"}
          {!submitting && <ArrowRight className="ml-2 h-4 w-4" />}
        </Button>
      </form>

      <div className="mt-6 text-sm text-slate-500">
        Already have an account?{" "}
        <Link to="/login" className="font-semibold text-[#2563EB] hover:underline" data-testid="signup-signin-link">Sign in</Link>
      </div>
    </Shell>
  );
}
